import { cn } from '@/lib/utils'
import { Wrench, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react'
import { useCallback, lazy, Suspense, useEffect, useState, type ReactNode } from 'react'
import { AppMarkdown } from '@/components/app-markdown'
import { TickerText } from './ticker-text'
import { splitMentionPreamble } from './mention-preamble'
import { CHAT_VISUAL_LANGS, parseChatVisual } from './chat-visuals'
import type { ChatVisualSize } from './chat-visual'

// Charts load only when an answer actually carries one (AG Charts + klinecharts).
const ChatVisualBlock = lazy(() => import('./chat-visual'))

function VisualPlaceholder({ text }: { text: string }) {
  return (
    <p className="not-prose my-2 rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
      {text}
    </p>
  )
}

/**
 * ```chart / ```price-chart blocks become charts (chat-visuals.ts). While the
 * block is still streaming its JSON is incomplete: show a placeholder, and
 * only call it unavailable once the answer has finished.
 */
function renderVisual(streaming: boolean, size?: ChatVisualSize) {
  return (lang: string, code: string): ReactNode | null => {
    if (!CHAT_VISUAL_LANGS.has(lang)) return null
    const visual = parseChatVisual(lang, code)
    if (!visual) {
      return (
        <VisualPlaceholder text={streaming ? 'Drawing chart…' : 'This chart could not be drawn.'} />
      )
    }
    return (
      <Suspense fallback={<VisualPlaceholder text="Drawing chart…" />}>
        <ChatVisualBlock visual={visual} size={size} />
      </Suspense>
    )
  }
}
import { fenceBareScripts } from '@edge/edgescript/answer'
import { LazyOpenScriptButton } from '@/features/edgescript/companion/lazy-open-script-button'
import { EDGESCRIPT_ENABLED } from '@/features/edgescript/flags'
import { FLAGS } from '@/services/analytics/feature-flags'
import { useFeatureFlag } from '@/services/analytics/use-feature-flag'

/** An EdgeScript block (the pragma is its first line) gets "Open on chart" under it. */
function scriptBlockAction(code: string) {
  return /^\s*\/\/@edgescript\b/.test(code) ? <LazyOpenScriptButton source={code} /> : null
}

interface ChatMessageProps {
  role: 'user' | 'assistant'
  text: string
  timestamp: number
  toolCalls?: Array<{ name: string; input: Record<string, unknown> }>
  toolResults?: Array<{ success: boolean; content: string }>
  /**
   * True while this text is still streaming in. Streaming text renders
   * through the SAME markdown pipeline as final messages (Chris,
   * 2026-08-24): formatting appears live as it streams; react-markdown
   * re-parses per delta and partial constructs render progressively.
   */
  streaming?: boolean
  /** Conversation-established symbols so lowercase mentions highlight too. */
  knownTickers?: ReadonlySet<string>
  /** How tall its charts draw: compact in the drawer, wide on a full page. */
  visualSize?: ChatVisualSize
}

/**
 * Copy the message's markdown source. The floating window trades text
 * selection for drag-anywhere, so this is how its answers get copied.
 */
function CopyMessageButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const id = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(id)
  }, [copied])
  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(text)
      .then(() => setCopied(true))
      .catch(() => {
        /* clipboard blocked: nothing to report */
      })
  }
  return (
    <button
      onClick={handleCopy}
      className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground/60 hover:bg-hover hover:text-foreground"
      title={copied ? 'Copied' : 'Copy'}
      aria-label={copied ? 'Copied' : 'Copy message'}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  )
}

/**
 * The user's words, with any `@`-mention preamble (the panel snapshot sent
 * to the model) reduced to small panel-name chips.
 */
function UserMessageText({
  text,
  knownTickers,
}: {
  text: string
  knownTickers?: ReadonlySet<string>
}) {
  const { panels, body } = splitMentionPreamble(text)
  return (
    <>
      {panels.length > 0 && (
        <div className="mb-1 flex flex-wrap gap-1">
          {panels.map((title, i) => (
            <span
              key={i}
              className="rounded bg-primary/15 px-1.5 py-0.5 text-[11px] font-medium text-primary"
            >
              @{title}
            </span>
          ))}
        </div>
      )}
      <p className="whitespace-pre-wrap break-words">
        <TickerText text={body} known={knownTickers} />
      </p>
    </>
  )
}

export function ChatMessage({
  role,
  text,
  timestamp,
  toolCalls,
  toolResults,
  streaming,
  knownTickers,
  visualSize,
}: ChatMessageProps) {
  const [toolsExpanded, setToolsExpanded] = useState(false)
  // "Open on chart" rides `edgescript-release` inside the build gate.
  const edgeScriptReleased = useFeatureFlag(FLAGS.EDGESCRIPT)
  const renderCodeBlock = useCallback(
    (lang: string, code: string) => renderVisual(!!streaming, visualSize)(lang, code),
    [streaming, visualSize],
  )
  const isUser = role === 'user'
  const time = new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  return (
    // No avatars (Chris, 2026-08-24): the narrow window needs every pixel
    // for content. The user's message is a right-aligned glass bubble
    // (Chris, 2026-08-28); assistant text flows plain at full width.
    <div className={cn('flex flex-col gap-1 px-3 py-2', isUser && 'items-end')}>
      <div
        className={cn(
          'text-sm leading-relaxed',
          isUser
            ? 'max-w-[85%] rounded-2xl border border-border/60 bg-muted/50 px-3 py-2 text-foreground shadow-sm backdrop-blur-sm'
            : 'w-full text-foreground',
        )}
      >
        {isUser ? (
          <UserMessageText text={text} knownTickers={knownTickers} />
        ) : (
          <AppMarkdown
            highlightTickers
            knownTickers={knownTickers}
            codeBlockAction={
              EDGESCRIPT_ENABLED && edgeScriptReleased ? scriptBlockAction : undefined
            }
            renderCodeBlock={renderCodeBlock}
            className="prose prose-sm max-w-none break-words text-foreground dark:prose-invert [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_h1]:text-base [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_table]:text-xs"
          >
            {/* a script answered without a fence renders as code, with its button */}
            {EDGESCRIPT_ENABLED && edgeScriptReleased ? fenceBareScripts(text) : text}
          </AppMarkdown>
        )}
      </div>

      {/* Tool calls */}
      {toolCalls && toolCalls.length > 0 && (
        <button
          onClick={() => setToolsExpanded((p) => !p)}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
        >
          <Wrench size={10} />
          {toolCalls.length} tool{toolCalls.length > 1 ? 's' : ''} used
          {toolsExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        </button>
      )}

      {toolsExpanded && toolCalls && (
        <div className="space-y-1 rounded border border-border/50 bg-muted/30 p-2">
          {toolCalls.map((tc, i) => (
            <div key={i} className="text-[11px]">
              <span className="font-mono font-medium text-primary">{tc.name}</span>
              {toolResults?.[i] && (
                <span
                  className={cn(
                    'ml-1.5',
                    toolResults[i].success ? 'text-green-500' : 'text-red-500',
                  )}
                >
                  {toolResults[i].success ? 'OK' : 'failed'}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1">
        <span className="text-[10px] text-muted-foreground/60">{time}</span>
        {!isUser && !streaming && text.trim() !== '' && <CopyMessageButton text={text} />}
      </div>
    </div>
  )
}
